import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Row, Col, Button, Spinner, Alert } from 'react-bootstrap';
import { saveOnboardingStep, getOnboardingStepData, isStepCompleted } from '../../utils/onboardingUtils';

const LeagueSelection = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [leagues, setLeagues] = useState([]);
    const [selectedLeague, setSelectedLeague] = useState(null);
    const [error, setError] = useState('');
    const [apiError, setApiError] = useState('');
    const [username, setUsername] = useState('');
    const [platformUserId, setPlatformUserId] = useState('');
    const [displayName, setDisplayName] = useState('');
    const history = useHistory();

    useEffect(() => {
        // Check if previous step is completed
        if (!isStepCompleted('username')) {
            history.push('/onboarding/username');
            return;
        }

        // Fetch leagues from Sleeper API
        const fetchLeagues = async () => {
            setIsLoading(true);
            setError('');
            setApiError('');
            
            try {
                // Get username from storage
                const usernameData = getOnboardingStepData('username');
                if (!usernameData || !usernameData.username) {
                    // No username data, redirect back to username step
                    history.push('/onboarding/username');
                    return;
                }
                
                const usernameToFetch = usernameData.username;
                setUsername(usernameToFetch);
                
                // Error handling for 'error' username - clear the error state and continue
                if (usernameToFetch === 'error') {
                    // Clear the error state from username step
                    saveOnboardingStep('username', {
                        username: usernameToFetch,
                        error: null
                    });
                    // Continue with the API call instead of redirecting
                }
                

                
                // Call Sleeper API to fetch leagues
                const response = await fetch('/api/post/sleeper/fetch-leagues', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ username: usernameToFetch }),
                });
                
                const data = await response.json();
                
                if (!response.ok) {
                    throw new Error(data.error || 'Failed to fetch leagues');
                }
                
                if (!data.success) {
                    throw new Error(data.error || 'API request failed');
                }
                
                // Extract league data from response
                const leaguesData = data.data.leagues || [];
                setLeagues(leaguesData);
                setPlatformUserId(data.data.platform_user_id);
                setDisplayName(data.data.display_name);
                

                
                if (leaguesData.length === 0) {
                    setApiError('No leagues found for this username. Please check your Sleeper username and try again.');
                }
                
            } catch (error) {
                console.error('❌ Error fetching leagues:', error);
                setApiError(error.message || 'Failed to fetch leagues. Please try again.');
                
                // If it's a username not found error, update the error state but don't redirect
                if (error.message.includes('not found') || error.message.includes('Username not found')) {
                    saveOnboardingStep('username', {
                        username: username,
                        error: 'Username not found on Sleeper. Please check and try again.'
                    });
                    // Don't redirect - let user see the error and decide what to do
                }
            } finally {
                setIsLoading(false);
            }
        };

        fetchLeagues();
    }, [history, username]);

    const handleLeagueSelect = (league) => {
        setSelectedLeague(league);
        setError(''); // Clear any previous errors
    };

    const handleSubmit = () => {
        if (!selectedLeague) {
            setError('Please select a league');
            return;
        }

        // Save selected league and user data to localStorage
        // This will be used later when the user authenticates
        saveOnboardingStep('leagues', { 
            username: username,
            platform_user_id: platformUserId,
            platform_name: 'sleeper', // Add the platform name
            display_name: displayName,
            selected_league: {
                platform_league_id: selectedLeague.platform_league_id,
                name: selectedLeague.name,
                season: selectedLeague.season,
                sport: selectedLeague.sport,
                status: selectedLeague.status,
                total_rosters: selectedLeague.total_rosters,
                season_type: selectedLeague.season_type
            },
            totalLeagues: leagues.length
        });
        
        // Navigate to next step
        history.push('/onboarding/auth');
    };

    const getProgressPercentage = () => {
        // Leagues is step 3 of 8, so (3-1)/8 = 25%
        return 25;
    };

    if (isLoading) {
        return (
            <div className="pt-5 onboarding-section">    
                <Row className="onboarding-header">
                    <Col xs={1} md={2}></Col>
                    <Col xs={10} md={8}>       
                            
                        <Row className="text-center onboarding-title-row">
                            <Col className="onboarding-title">Fetching Your Leagues</Col>
                        </Row>
                        
                        {/* Progress Bar */}
                        <Row className="mt-4">
                            <Col>
                                <div className="text-center mb-1">
                                    <span className="onboarding-progress-percentage">
                                        {Math.round(getProgressPercentage())}% Complete
                                    </span>
                                </div>
                                <div className="onboarding-progress">
                                    <div 
                                        className="onboarding-progress-bar"
                                        style={{ width: `${getProgressPercentage()}%` }}
                                    ></div>
                                </div>
                            </Col>
                        </Row>

                        <Row className="pt-4">
                            <Col>
                                <div className="text-center">
                                    <Spinner animation="border" role="status" className="mb-3">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <h4 className="text-white">Processing...</h4>
                                </div>
                            </Col>
                        </Row>
                    </Col>                            
                </Row>
            </div>
        );
    }

    return (
        <div className="pt-5 onboarding-section">    
            <Row className="onboarding-header">
                <Col xs={1} md={2}></Col>
                <Col xs={10} md={8}>       
                        
                    <Row className="text-center onboarding-title-row">
                        <Col className="onboarding-title">Select Your League</Col>
                    </Row>
                    
                    {/* Progress Bar */}
                    <Row className="mt-4">
                        <Col>
                            <div className="text-center mb-2">
                                <span className="onboarding-progress-percentage">
                                    {Math.round(getProgressPercentage())}% Complete
                                </span>
                            </div>
                            <div className="onboarding-progress">
                                <div 
                                    className="onboarding-progress-bar"
                                    style={{ width: `${getProgressPercentage()}%` }}
                                ></div>
                            </div>
                        </Col>
                    </Row>

                    <Row className="pt-4">
                        <Col>
                            {/* Display API errors */}
                            {apiError && (
                                <Alert variant="danger" className="mb-4">
                                    <strong>Error:</strong> {apiError}
                                </Alert>
                            )}
                            
                            {/* Display leagues */}
                            {leagues.length === 0 && !apiError ? (
                                <div className="text-center py-4">
                                    <p className="text-muted">No leagues found for this username.</p>
                                    <p className="text-muted">Please check your Sleeper username and try again.</p>
                                </div>
                            ) : (
                                <div className="league-options">
                                    {leagues.map(league => (
                                    <div 
                                        key={league.platform_league_id} 
                                        className={`league-card mb-4 ${selectedLeague && selectedLeague.platform_league_id === league.platform_league_id ? 'selected' : ''}`}
                                        onClick={() => handleLeagueSelect(league)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                handleLeagueSelect(league);
                                            }
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Select ${league.name} league`}
                                        aria-pressed={selectedLeague && selectedLeague.platform_league_id === league.platform_league_id}
                                    >
                                        <div className="text-center">
                                            <h4 className="league-name mb-2">{league.name}</h4>
                                            <div className="league-details">
                                                <small className="text-muted">
                                                    {league.total_rosters} teams
                                                </small>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                </div>
                            )}

                            {error && (
                                <div className="text-center mt-3">
                                    <span className="error-message">
                                        {error}
                                    </span>
                                </div>
                            )}

                            <Row className='mt-5 justify-content-center'>
                                <Col xs="auto">
                                    <Button 
                                        type="button"
                                        onClick={() => history.push('/onboarding/username')}
                                        className="btn-secondary btn-lg mx-2 onboarding-nav-btn"
                                    >
                                        Back
                                    </Button>
                                </Col>
                                <Col xs="auto">
                                    <Button 
                                        onClick={handleSubmit}
                                        disabled={!selectedLeague}
                                        className="btn-primary-gradient btn-lg mx-2 onboarding-nav-btn"
                                    >
                                        Next →
                                    </Button>
                                </Col>
                            </Row>
                        </Col>
                    </Row>                        
                </Col>                            
            </Row>
        </div>
    );
};

export default LeagueSelection;
